<template>
	<v-menu v-model="isFilterOpen" :offset="4" :close-on-content-click="false" scroll-strategy="block" content-class="menu-content">
		<template #activator="{ props }">
			<slot name="button" :props="props"> Date Created </slot>
		</template>
		<v-card class="pa-0" rounded="lg" elevation="8" @click.stop>
			<!-- LSP-15908: when the v-menu effectively covers the whole screen on
			     phone-width there's no obvious dismiss affordance — give mobile users
			     an explicit close button so they aren't stuck guessing where to tap. -->
			<div
				v-if="display.smAndDown.value"
				class="d-flex align-center justify-space-between px-3 pt-3"
				data-test-id="date-filter-mobile-header">
				<span class="text-subtitle-2 font-weight-bold">Filter by date</span>
				<v-btn
					icon="icon-close-medium"
					variant="text"
					density="comfortable"
					aria-label="Close date filter"
					data-test-id="date-filter-close-button"
					@click="close" />
			</div>
			<v-card-actions class="d-flex flex-no-wrap ga-2 flex-column">
				<div class="d-flex flex-row ga-2 w-100">
					<v-btn
						class="date-filter-item flex-grow-1 flex-1-1-100"
						:class="{ active: selectedDateFilter === 'today' }"
						color="grey"
						variant="tonal"
						@click="selectedDateFilter = 'today'">
						<div class="d-flex check">
							<v-icon> icon-check-big</v-icon>
						</div>
						Today
					</v-btn>
					<v-btn
						class="date-filter-item flex-grow-1 flex-1-1-100"
						:class="{ active: selectedDateFilter === 'last-7-days' }"
						color="grey"
						variant="tonal"
						@click="selectedDateFilter = 'last-7-days'">
						<div class="d-flex check">
							<v-icon> icon-check-big</v-icon>
						</div>
						Last 7 Days</v-btn
					>
				</div>
				<div class="d-flex flex-row ga-2 w-100">
					<v-btn
						class="date-filter-item flex-grow-1 flex-1-1-100"
						:class="{ active: selectedDateFilter === 'last-30-days' }"
						color="grey"
						variant="tonal"
						@click="selectedDateFilter = 'last-30-days'">
						<div class="d-flex check">
							<v-icon> icon-check-big</v-icon>
						</div>
						Last 30 Days
					</v-btn>
					<v-btn
						class="date-filter-item flex-grow-1 flex-1-1-100"
						:class="{ active: selectedDateFilter === 'custom' }"
						color="grey"
						variant="tonal"
						@click="selectedDateFilter = 'custom'">
						<div class="d-flex check">
							<v-icon> icon-check-big</v-icon>
						</div>
						Custom
					</v-btn>
				</div>
			</v-card-actions>
			<v-card-text v-if="selectedDateFilter === 'custom'" class="d-flex flex-column justify-center align-center">
				<div class="d-flex justify-space-around ga-2 selected-date-container w-100">
					<div
						class="date-pill py-2 ga-2 d-flex flex-grow-1 flex-shrink-1"
						:class="{ active: currentDateSelected === 'start', invalid: !!validationMessage }"
						data-test-id="date-filter-start-date"
						@click="currentDateSelected = 'start'">
						<img v-if="startDate" src="@/assets/search/icon-filter-calendar.svg" alt="calendar" />
						{{ startDateFormatted ?? "Select a Start Date" }}
					</div>
					<div
						class="date-pill py-2 ga-2 d-flex flex-grow-1 flex-shrink-1"
						:class="{ active: currentDateSelected === 'end', invalid: !!validationMessage }"
						data-test-id="date-filter-end-date"
						@click="currentDateSelected = 'end'">
						<img v-if="endDate" src="@/assets/search/icon-filter-calendar.svg" alt="calendar" />
						{{ endDateFormatted ?? "Select an End Date" }}
					</div>
				</div>

				<!-- LSP-20610: the Apply action must either apply the filter or say why it cannot. It used
				     to silently return when a date was missing, which read as a dead button. One date is
				     now a valid single-day filter, so the only unappliable state left is "no date at all",
				     and that state gets a prominent error message rather than silence. -->
				<div
					v-if="validationMessage"
					class="date-filter-validation w-100"
					role="alert"
					aria-live="assertive"
					data-test-id="date-filter-validation">
					<v-icon size="14">icon-warning-triangle</v-icon>
					{{ validationMessage }}
				</div>
				<div v-else class="date-filter-hint w-100" data-test-id="date-filter-hint">
					Pick one date for a single day, or two for a range.
				</div>

				<v-date-picker
					v-if="initialized"
					:model-value="dateRange"
					multiple
					control-variant="modal"
					:hide-header="true"
					color="primary"
					class="d-flex flex-grow-1"
					:allowed-dates="(date: any) => moment(date).isBefore(moment().endOf('day').subtract(1, 'second'))"
					:max="moment().endOf('day').subtract(1, 'second').format('YYYY-MM-DD')"
					@update:modelValue="dateRangeUpdated"></v-date-picker>
			</v-card-text>
			<div v-else :style="{ width: display.smAndDown.value ? '100vw' : 'unset' }"></div>
			<v-card-actions class="d-flex justify-end">
				<v-btn color="grey" text rounded="xl" elevation="0" class="white-bg" @click="clearFilter(true)">Clear</v-btn>
				<PrimaryBtn @click="applyFilter">Apply</PrimaryBtn>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
	import { computed, nextTick, onMounted, PropType, ref, watch } from "vue";
	import type { DateFilterType } from "@/types/search/DateFilterType";
	import moment, { Moment } from "moment";
	import { useDisplay } from "vuetify";

	const isFilterOpen = ref(false);
	const selectedDateFilter = ref<DateFilterType>("custom");
	const dateRange = ref<Array<string | undefined>>([]);
	const appliedDateRange = ref<Array<string | undefined>>([]);
	const orderedDates = ref<Moment[]>([]);
	const startDate = ref<string | undefined>();
	const endDate = ref<string | undefined>();
	const currentDateSelected = ref<"start" | "end">("start");
	const initialized = ref(false);
	const hasAppliedFilter = ref(false);
	// LSP-20610: set only when Apply is pressed with nothing selected; cleared the moment the user
	// picks a date, clears, or reopens the menu, so a stale error can never outlive the state it names.
	const validationMessage = ref<string | undefined>();
	const display = useDisplay();

	const props = defineProps({
		preSelectedDateFilter: {
			type: String as PropType<DateFilterType>,
			required: false
		},
		startDate: {
			type: Date,
			required: false
		},
		endDate: {
			type: Date,
			required: false
		}
	});

	const emit = defineEmits<{
		(e: "filter-dates", startDate: Moment, endDate: Moment, dateFilter: DateFilterType): void;
		(e: "clear-dates"): void;
	}>();

	function restore() {
		selectedDateFilter.value = props.preSelectedDateFilter ?? "custom";
		hasAppliedFilter.value = false;

		if (props.startDate && props.endDate) {
			setDates(moment(props.startDate).format("YYYY-MM-DD"), moment(props.endDate).format("YYYY-MM-DD"));
			hasAppliedFilter.value = true;
		} else if (props.preSelectedDateFilter && props.preSelectedDateFilter !== "custom") {
			hasAppliedFilter.value = true;
		}
	}

	defineExpose({
		isFilterOpen,
		clear: clearFilter,
		restore
	});

	const startDateFormatted = computed(() => {
		if (startDate.value) {
			return moment(startDate.value).format("DD MMM YYYY");
		}
		return null;
	});

	const endDateFormatted = computed(() => {
		if (endDate.value) {
			return moment(endDate.value).format("DD MMM YYYY");
		}
		return null;
	});

	function setDates(start: string | undefined, end: string | undefined) {
		startDate.value = start;
		endDate.value = end;
		if (start || end) validationMessage.value = undefined;

		if (!start) {
			// LSP-15682 follow-up (cursor-bot): the "End set, Start undefined" combination
			// is newly reachable after the highlight-pill fix above. `v-date-picker` reads
			// `dateRange` for the in-calendar highlight, so feed it the single end date
			// here too — otherwise the End pill text updates but the calendar grid stays
			// blank, leaving the user with no visual confirmation of the click.
			dateRange.value = end ? [end] : [];
			return;
		}

		if (!end) {
			dateRange.value = [start];
			return;
		}

		// calculate the range of dates between start and end
		const range = moment(end).diff(moment(start), "days");
		const dates = [];
		for (let i = 0; i < range; i++) {
			dates.push(moment(start).add(i, "days").format("YYYY-MM-DD"));
		}
		dates.push(end);
		dateRange.value = dates;
	}

	onMounted(() => {
		selectedDateFilter.value = props.preSelectedDateFilter ?? "custom";
		if (props.preSelectedDateFilter !== "custom") {
			hasAppliedFilter.value = true;
		}

		if (props.startDate && props.endDate) {
			setDates(moment(props.startDate).format("YYYY-MM-DD"), moment(props.endDate).format("YYYY-MM-DD"));
			hasAppliedFilter.value = true;
		}

		initialized.value = true;
	});

	watch(
		() => props.preSelectedDateFilter,
		(newVal) => {
			selectedDateFilter.value = newVal ?? "custom";
		}
	);

	watch(isFilterOpen, (newVal) => {
		validationMessage.value = undefined;
		if (!newVal) {
			setDates(undefined, undefined);
			currentDateSelected.value = "start";
		} else {
			if (props.startDate && props.endDate) {
				setDates(moment(props.startDate).format("YYYY-MM-DD"), moment(props.endDate).format("YYYY-MM-DD"));
			}

			selectedDateFilter.value = props.preSelectedDateFilter ?? "custom";
		}
	});

	watch(
		() => [props.startDate, props.endDate],
		([newStartDate, newEndDate]) => {
			let newStart = newStartDate ? moment(newStartDate).format("YYYY-MM-DD") : undefined;
			let newEnd = newEndDate ? moment(newEndDate).format("YYYY-MM-DD") : undefined;

			// Validate and swap dates if necessary
			if (newStart && newEnd && newStart > newEnd) {
				const temp = newStart;
				newStart = newEnd;
				newEnd = temp;
			}

			setDates(newStart, newEnd);
		},
		{ immediate: false }
	);

	// Watch for changes in startDate and endDate to ensure they stay in correct order
	watch(
		() => [startDate.value, endDate.value],
		([newStart, newEnd]) => {
			if (newStart && newEnd && newStart > newEnd) {
				// Swap the dates silently
				const temp = startDate.value;
				setDates(endDate.value, temp);
			}
		},
		{ immediate: false }
	);

	const dateRangeUpdated = (dates: (string | undefined)[]) => {
		// Normalization helper
		const normalize = (d: any) => (d ? moment(d).format("YYYY-MM-DD") : undefined);
		const oldSet = new Set((dateRange.value || []).map(normalize).filter(Boolean) as string[]);
		const newSet = new Set(dates.map(normalize).filter(Boolean) as string[]);

		let clicked: string | undefined;

		// Check for added dates (User clicked a date not currently selected)
		for (const d of newSet) {
			if (!oldSet.has(d)) {
				clicked = d;
				break;
			}
		}

		// Check for removed dates (User clicked a date currently selected)
		if (!clicked) {
			for (const d of oldSet) {
				if (!newSet.has(d)) {
					clicked = d;
					break;
				}
			}
		}

		if (!clicked) return;

		const clickedMoment = moment(clicked);
		const currentStart = startDate.value ? moment(startDate.value) : undefined;
		const currentEnd = endDate.value ? moment(endDate.value) : undefined;

		let newStart = startDate.value;
		let newEnd = endDate.value;
		let nextSelection = currentDateSelected.value == "start" ? "end" : "start";

		if (!currentStart) {
			// LSP-15682: when the user highlights the End Date pill first and clicks a
			// calendar day, that click must land in End — not Start. Previously this
			// branch always wrote the click to Start, ignoring `currentDateSelected`,
			// so the highlighted pill and the populated pill always disagreed.
			if (currentDateSelected.value === "end") {
				newEnd = clicked;
			} else {
				newStart = clicked;
			}
		} else if (!currentEnd) {
			if (clickedMoment.isBefore(currentStart)) {
				newStart = clicked;
			} else {
				newEnd = clicked;
			}
		} else {
			// Both selected
			if (currentDateSelected.value === "end") {
				if (clickedMoment.isBefore(currentStart)) {
					// User clicked strictly before Start -> Update Start, and make old start the new end
					newStart = clicked;
					newEnd = startDate.value;
				} else {
					// User clicked on or after Start -> Update End
					newEnd = clicked;
				}
			} else {
				// currentDateSelected === "start"
				if (clickedMoment.isAfter(currentEnd)) {
					// User clicked strictly after End -> Update End, and make old end the new start
					newStart = endDate.value;
					newEnd = clicked;
				} else {
					// User clicked on or before End -> Update Start
					newStart = clicked;
				}
			}
		}

		setDates(newStart, newEnd);
		currentDateSelected.value = nextSelection as "start" | "end";
	};

	function applyFilter() {
		let start: Moment;
		let end: Moment;

		if (selectedDateFilter.value === "custom") {
			// LSP-20610: a single selected date is a single-day filter, not an incomplete range. The
			// user should never have to click the same day twice, so whichever side is missing
			// borrows the one that is set. Only "nothing selected at all" is unappliable.
			const singleDate = startDate.value ?? endDate.value;
			if (!singleDate) {
				validationMessage.value = "Select a date to apply this filter.";
				currentDateSelected.value = "start";
				return;
			}
			validationMessage.value = undefined;
			start = moment(startDate.value ?? singleDate, "YYYY-MM-DD");
			end = moment(endDate.value ?? singleDate, "YYYY-MM-DD");
		} else {
			switch (selectedDateFilter.value) {
				case "today":
					start = moment().startOf("day");
					end = moment().endOf("day");
					break;
				case "last-7-days":
					start = moment().subtract(7, "days");
					end = moment();
					break;
				default:
					start = moment().subtract(30, "days");
					end = moment();
					break;
			}
		}

		emit("filter-dates", start.startOf("day"), end.endOf("day"), selectedDateFilter.value);
		appliedDateRange.value = [...dateRange.value];
		hasAppliedFilter.value = true;
		close();
	}

	function clearFilter(emitEvent: boolean = false) {
		dateRange.value = [];
		appliedDateRange.value = [];
		startDate.value = undefined;
		endDate.value = undefined;
		orderedDates.value = [];
		selectedDateFilter.value = "custom";
		currentDateSelected.value = "start";
		validationMessage.value = undefined;
		if (emitEvent && hasAppliedFilter.value) {
			emit("clear-dates");
		}
		hasAppliedFilter.value = false;
		close();
	}

	function close() {
		isFilterOpen.value = false;
	}
</script>
<style scoped>
	/* The picker selects a range as two picked days plus the days between. Days inside a run of
	   selected days fade to half opacity; the two ends of the run, and any day at a row edge, stay
	   solid, so the range reads as start and end. Moved here from the global page.scss: this is the
	   only multi-select date picker in admin. */
	:deep(div.v-date-picker-month__days > .v-date-picker-month__day--selected) {
		opacity: 0.5 !important;
	}
	:deep(div.v-date-picker-month__days > .v-date-picker-month__day--selected:first-child),
	:deep(div.v-date-picker-month__days > .v-date-picker-month__day--selected:last-child),
	:deep(div.v-date-picker-month__days > .v-date-picker-month__day--selected:has(+ :not(.v-date-picker-month__day--selected))),
	:deep(div.v-date-picker-month__days > :not(.v-date-picker-month__day--selected) + .v-date-picker-month__day--selected) {
		opacity: 1 !important;
	}

	.date-filter-item.v-btn {
		font-size: 0.7rem !important;
		font-weight: 400 !important;
		border-radius: 10px;
		padding: 0 16px !important;
		min-width: 140px;
	}

	button.date-filter-item.v-btn:deep(span.v-btn__content) {
		color: rgb(0, 0, 0);
	}

	.date-filter-item.v-btn .v-icon {
		font-size: 0.75rem !important;
	}

	.date-filter-item.v-btn.active {
		background-color: var(--color-primary);
	}
	.date-filter-item.v-btn.active:deep(span.v-btn__content) {
		color: #fff;
	}

	.date-filter-item.v-btn.active .v-icon {
		color: #fff;
	}

	.date-filter-item.v-btn .check {
		background-color: #d9d9d9;
		height: 16px;
		width: 16px;
		border-radius: 50%;
		text-align: center;
		display: flex;
		align-items: center;
		justify-content: center;
		margin-right: 4px;
	}

	.date-filter-item.v-btn.active .check {
		background-color: #5ca4ff;
	}

	.selected-date-container {
		gap: 12px;
	}

	.date-pill {
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 1;
		background-color: #f6f6f6;
		color: var(--color-primary);
		border-radius: 10px;
		border: 1px solid #b6b6b6;
		cursor: pointer;
	}

	.date-pill.active {
		border: 2px solid #0772f1;
	}

	/* LSP-20610: the error state has to be distinguishable from the picker's own blue selection
	   styling, so it uses the error red on both the border and the message rather than another blue. */
	.date-pill.invalid {
		border: 2px solid rgb(var(--v-theme-error));
		color: rgb(var(--v-theme-error));
	}

	.date-filter-validation {
		display: flex;
		align-items: center;
		gap: 6px;
		margin-top: 8px;
		font-size: 0.75rem;
		font-weight: 600;
		color: rgb(var(--v-theme-error));
	}

	.date-filter-hint {
		margin-top: 8px;
		font-size: 0.75rem;
		color: rgba(0, 0, 0, 0.6);
	}

	.date-pill img {
		height: 20px;
	}

	/*.v-picker--date :deep(.v-btn) {
		background-color: var(--color-primary);
	}

	.v-picker--date :deep(.v-btn::before) {
		opacity: unset;
		color: white;
	} */
</style>
